<template>
  <div class="attachment-manager">
    <!-- Upload Section -->
    <div class="upload-section mb-4">
      <div class="upload-buttons">
        <input
          ref="certificateFile"
          type="file"
          @change="handleFileSelect"
          accept=".pdf,.jpg,.jpeg,.png,.gif"
          style="display: none"
          data-type="certificate"
        />
        <input
          ref="documentFile"
          type="file"
          @change="handleFileSelect"
          accept=".pdf,.doc,.docx"
          style="display: none"
          data-type="document"
        />
        <input
          ref="materialFile"
          type="file"
          @change="handleFileSelect"
          accept="image/*"
          style="display: none"
          data-type="material_photo"
        />

        <v-btn
          color="primary"
          outlined
          small
          class="mr-2 mb-2"
          @click="$refs.certificateFile.click()"
        >
          <v-icon small left>mdi-file-pdf-box</v-icon>
          อัปโหลดวุฒิบัตร
        </v-btn>
        <v-btn
          color="primary"
          outlined
          small
          class="mr-2 mb-2"
          @click="$refs.documentFile.click()"
        >
          <v-icon small left>mdi-file-document</v-icon>
          อัปโหลดเอกสาร
        </v-btn>
        <v-btn
          color="primary"
          outlined
          small
          class="mb-2"
          @click="$refs.materialFile.click()"
        >
          <v-icon small left>mdi-image</v-icon>
          อัปโหลดรูปภาพ
        </v-btn>
      </div>
    </div>

    <!-- New Files Section -->
    <v-expand-transition>
      <v-row v-if="newFiles.length > 0" class="mb-4">
        <v-col cols="12">
          <v-card flat outlined class="file-section">
            <v-card-title class="file-section-title">
              <v-icon small class="mr-2" color="primary">mdi-plus-circle</v-icon>
              ไฟล์ใหม่ ({{ newFiles.length }})
            </v-card-title>
            <v-divider></v-divider>
            <v-list>
              <v-list-item
                v-for="(file, idx) in newFiles"
                :key="`new-${idx}`"
                class="file-list-item new-file-item"
              >
                <v-list-item-icon>
                  <v-badge
                    color="primary"
                    content="new"
                    overlap
                    offset-x="15"
                    offset-y="5"
                  >
                    <v-icon :color="getFileTypeColor(file.file_type)">
                      {{ getFileTypeIcon(file.file_type) }}
                    </v-icon>
                  </v-badge>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-500">
                    {{ file.file_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span class="file-type-badge">
                      {{ formatFileType(file.file_type) }}
                    </span>
                    <span class="file-size ml-2">
                      {{ formatFileSize(file.file.size) }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="removeNewFile(idx)"
                      >
                        <v-icon small color="error">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>ลบไฟล์</span>
                  </v-tooltip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- Existing Files Section -->
    <v-expand-transition>
      <v-row v-if="existingFiles.length > 0" class="mb-4">
        <v-col cols="12">
          <v-card flat outlined class="file-section">
            <v-card-title class="file-section-title">
              <v-icon small class="mr-2" color="success">mdi-check-circle</v-icon>
              ไฟล์แนบ ({{ existingFiles.length }})
            </v-card-title>
            <v-divider></v-divider>
            <v-list>
              <v-list-item
                v-for="file in existingFiles"
                :key="file.id"
                class="file-list-item existing-file-item"
              >
                <v-list-item-icon>
                  <v-icon :color="getFileTypeColor(file.file_type)">
                    {{ getFileTypeIcon(file.file_type) }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-500">
                    {{ file.display_name || file.file_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span class="file-type-badge">
                      {{ formatFileType(file.file_type) }}
                    </span>
                    <span class="file-size ml-2">
                      {{ formatFileSize(file.file_size) }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="previewFile(file)"
                        :loading="loadingPreview === file.id"
                      >
                        <v-icon small color="info">mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>ดูตัวอย่าง</span>
                  </v-tooltip>
                </v-list-item-action>
                <v-list-item-action>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="downloadFile(file)"
                      >
                        <v-icon small color="primary">mdi-download</v-icon>
                      </v-btn>
                    </template>
                    <span>ดาวน์โหลด</span>
                  </v-tooltip>
                </v-list-item-action>
                <v-list-item-action>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="confirmDeleteFile(file)"
                        :loading="deletingFile === file.id"
                      >
                        <v-icon small color="error">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>ลบไฟล์</span>
                  </v-tooltip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- File Preview Dialog -->
    <v-dialog
      v-model="previewDialog"
      max-width="900px"
      content-class="preview-dialog"
    >
      <v-card class="preview-card">
        <v-card-title class="preview-header">
          <v-icon class="mr-3" color="white" size="24">mdi-file-document</v-icon>
          <span class="preview-title">ตัวอย่างไฟล์ {{  previewFileData.file_path }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="preview-content">
          <!-- PDF Preview -->
          <div v-if="previewFileData && (previewFileData.file_type === 'pdf' || isPdfFile(previewFileData))">
            <iframe
              :src="'/hrvec_api/'+previewFileData.file_path"
              width="100%"
              height="600"
              style="border: 1px solid #e0e0e0; border-radius: 8px"
            ></iframe>
          </div>

          <!-- Image Preview -->
          <div v-else-if="previewFileData && isImageFile(previewFileData)" class="image-preview">
            <img
              :src="'/hrvec_api/'+previewFileData.file_path"
              :alt="previewFileData.file_name"
              class="preview-image"
            />
          </div>

          <!-- Document Preview -->
          <div v-else class="document-preview">
            <v-alert type="info" outlined class="mb-4">
              <v-icon small class="mr-2">mdi-information-outline</v-icon>
              <span v-if="previewFileData">
                ไฟล์ {{ formatFileType(previewFileData.file_type) }}
                ไม่สามารถแสดงตัวอย่างได้ โปรดดาวน์โหลดเพื่อดู
              </span>
            </v-alert>
            <v-btn
              v-if="previewFileData"
              color="primary"
              @click="downloadFile(previewFileData)"
              class="full-width-btn"
            >
              <v-icon small left>mdi-download</v-icon>
              ดาวน์โหลดไฟล์
            </v-btn>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="preview-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="previewDialog = false">ปิด</v-btn>
          <v-btn
            v-if="previewFileData"
            color="primary"
            depressed
            @click="downloadFile(previewFileData)"
          >
            <v-icon small left>mdi-download</v-icon>
            ดาวน์โหลด
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteFileDialog" persistent max-width="500px">
      <v-card>
        <v-card-title class="delete-header">
          <v-icon class="mr-3" color="white" size="24">mdi-delete-alert</v-icon>
          <span>ยืนยันการลบไฟล์</span>
        </v-card-title>

        <v-card-text class="delete-content">
          <div class="delete-icon-wrapper">
            <v-icon size="64" color="#dc2626">mdi-alert-circle-outline</v-icon>
          </div>
          <p class="delete-message">
            คุณต้องการลบไฟล์นี้หรือไม่?
          </p>
          <v-card outlined class="mt-4">
            <v-card-text>
              <div class="file-info">
                <v-icon
                  :color="getFileTypeColor(fileToDelete.file_type)"
                  class="mr-2"
                >
                  {{ getFileTypeIcon(fileToDelete.file_type) }}
                </v-icon>
                <span class="font-weight-500">
                  {{ fileToDelete.display_name || fileToDelete.file_name }}
                </span>
              </div>
            </v-card-text>
          </v-card>
          <p class="warning-text mt-4">
            <v-icon small color="#f97316" class="mr-1">mdi-alert</v-icon>
            การลบไฟล์นี้ไม่สามารถย้อนกลับได้
          </p>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteFileDialog = false">ยกเลิก</v-btn>
          <v-btn
            color="error"
            depressed
            @click="deleteFileConfirmed"
            :loading="deletingFile === fileToDelete.id"
          >
            <v-icon small left>mdi-delete-outline</v-icon>
            ลบไฟล์
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3" color="white">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'BusinessTripAttachmentManager',
  props: {
    files: {
      type: Array,
      default: () => []
    },
    ApiKey: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      newFiles: [],
      existingFiles: [],
      previewDialog: false,
      deleteFileDialog: false,
      previewFileData: {},
      fileToDelete: {},
      loadingPreview: null,
      deletingFile: null,
      snackbar: {
        show: false,
        text: '',
        color: '',
        icon: '',
        timeout: 4000
      }
    };
  },

  watch: {
    files: {
      handler(newVal) {
        this.loadExistingFiles();
      },
      immediate: true
    }
  },

  methods: {
    // ========== File Upload Methods ==========
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // ตรวจสอบขนาดไฟล์ (สูงสุด 10MB)
      const MAX_FILE_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        this.showSnackbar(
          'ไฟล์มีขนาดใหญ่เกินไป (สูงสุด 10MB)',
          'error',
          'mdi-alert'
        );
        return;
      }

      // ตรวจสอบชนิดไฟล์
      const fileType = event.target.getAttribute('data-type');
      const allowedExtensions = {
        certificate: ['pdf', 'jpg', 'jpeg', 'png', 'gif'],
        document: ['pdf', 'doc', 'docx'],
        material_photo: ['jpg', 'jpeg', 'png', 'gif']
      };

      const fileExtension = file.name
        .split('.')
        .pop()
        .toLowerCase();
      const allowed = allowedExtensions[fileType] || [];

      if (!allowed.includes(fileExtension)) {
        this.showSnackbar(
          `ชนิดไฟล์ไม่อนุญาต สำหรับ ${fileType}`,
          'error',
          'mdi-alert'
        );
        return;
      }

      // ตรวจสอบไม่ให้อัพโหลดไฟล์ซ้ำ
      const isDuplicate = this.newFiles.some((f) => f.file_name === file.name);
      if (isDuplicate) {
        this.showSnackbar(
          'ไฟล์นี้ได้ถูกอัปโหลดแล้ว',
          'warning',
          'mdi-alert'
        );
        return;
      }

      this.newFiles.push({
        file_name: file.name,
        display_name: file.name.split('.')[0],
        file_type: fileType,
        file: file,
        isNew: true
      });

      this.showSnackbar(
        `เพิ่มไฟล์ ${file.name} สำเร็จ`,
        'success',
        'mdi-check-circle'
      );

      event.target.value = '';
    },

    removeNewFile(index) {
      const file = this.newFiles[index];
      this.newFiles.splice(index, 1);
      this.$emit('file-removed', file);
      this.showSnackbar(
        `ลบไฟล์ ${file.file_name} สำเร็จ`,
        'info',
        'mdi-information'
      );
    },

    loadExistingFiles() {
      this.existingFiles = Array.isArray(this.files)
        ? this.files.filter((f) => !f.isNew)
        : [];
    },

    // ========== File Preview & Download ==========
    previewFile(file) {
      this.previewFileData = file;
      this.loadingPreview = file.id;

      // Simulate loading
      setTimeout(() => {
        this.previewDialog = true;
        this.loadingPreview = null;
      }, 300);
    },

    downloadFile(file) {
      const link = document.createElement('a');
      link.href = file.file_path;
      link.download = file.file_name;
      link.click();
    },

    // ========== File Deletion ==========
    confirmDeleteFile(file) {
      this.fileToDelete = file;
      this.deleteFileDialog = true;
    },

    async deleteFileConfirmed() {
      this.deletingFile = this.fileToDelete.id;

      try {
        const result = await this.$http.post(
          'gov_business_trip_report_delete_file.php',
          {
            ApiKey: this.ApiKey,
            attachment_id: this.fileToDelete.id
          }
        );

        if (result.data.status) {
          // ลบออกจาก array
          const index = this.existingFiles.findIndex(
            (f) => f.id === this.fileToDelete.id
          );
          if (index > -1) {
            this.existingFiles.splice(index, 1);
          }

          this.$emit('file-deleted', this.fileToDelete);
          this.deleteFileDialog = false;
          this.showSnackbar(
            'ลบไฟล์สำเร็จ',
            'success',
            'mdi-check-circle'
          );
        } else {
          this.showSnackbar(
            result.data.message || 'ไม่สามารถลบไฟล์ได้',
            'error',
            'mdi-alert'
          );
        }
      } catch (error) {
        console.error('Delete file error:', error);
        this.showSnackbar(
          'เกิดข้อผิดพลาดในการลบไฟล์',
          'error',
          'mdi-alert'
        );
      } finally {
        this.deletingFile = null;
      }
    },

    // ========== File Type Helpers ==========
    formatFileType(type) {
      const types = {
        certificate: 'วุฒิบัตร',
        document: 'เอกสาร',
        material_photo: 'รูปภาพ',
        other: 'อื่น ๆ'
      };
      return types[type] || type;
    },

    formatFileSize(bytes) {
      if (!bytes) return '-';
      const kb = bytes / 1024;
      if (kb < 1024) {
        return kb.toFixed(2) + ' KB';
      }
      const mb = kb / 1024;
      return mb.toFixed(2) + ' MB';
    },

    getFileTypeColor(type) {
      const colors = {
        certificate: '#dc2626',
        document: '#2563eb',
        material_photo: '#10b981',
        other: '#6b7280'
      };
      return colors[type] || '#6b7280';
    },

    getFileTypeIcon(type) {
      const icons = {
        certificate: 'mdi-file-certificate',
        document: 'mdi-file-document',
        material_photo: 'mdi-file-image',
        other: 'mdi-file'
      };
      return icons[type] || 'mdi-file';
    },

    isPdfFile(file) {
      if (!file || !file.file_name) return false;
      const ext = file.file_name.split('.').pop().toLowerCase();
      return ext === 'pdf';
    },

    isImageFile(file) {
      if (!file || !file.file_name) return false;
      const ext = file.file_name.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
    },

    // ========== Snackbar ==========
    showSnackbar(text, color, icon) {
      this.snackbar = {
        show: true,
        text,
        color,
        icon,
        timeout: 4000
      };
    },

    // ========== Public Methods ==========
    getNewFiles() {
      return this.newFiles;
    },

    resetFiles() {
      this.newFiles = [];
      this.loadExistingFiles();
    }
  }
};
</script>

<style scoped>
.attachment-manager {
  width: 100%;
}

.upload-section {
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
}

.upload-section:hover {
  border-color: #2563eb;
  background: #eff6ff;
}

.upload-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-section {
  background: white !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 12px !important;
  overflow: hidden;
}

.file-section-title {
  padding: 14px 16px;
  background: #f9fafb;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.file-list-item {
  border-bottom: 1px solid #f3f4f6 !important;
  transition: all 0.2s ease;
}

.file-list-item:hover {
  background: #f9fafb !important;
}

.file-list-item:last-child {
  border-bottom: none !important;
}

.new-file-item {
  background: #f0fdf4;
}

.existing-file-item {
  background: white;
}

.file-type-badge {
  display: inline-block;
  background: #eff6ff;
  color: #1e40af;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
}

.file-size {
  color: #9ca3af;
  font-size: 12px;
}

/* ========== Preview Dialog ========== */
.preview-dialog {
  border-radius: 16px !important;
}

.preview-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.preview-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  font-weight: 600;
}

.preview-title {
  font-size: 16px;
}

.preview-content {
  padding: 24px;
  max-height: 700px;
  overflow-y: auto;
  background: #fafafa;
}

.image-preview {
  text-align: center;
  padding: 24px;
  background: white;
  border-radius: 12px;
}

.preview-image {
  max-width: 100%;
  max-height: 600px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.document-preview {
  padding: 16px;
  background: white;
  border-radius: 12px;
}

.preview-actions {
  padding: 16px;
  background: #fafafa;
}

/* ========== Delete Dialog ========== */
.delete-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
  color: white;
}

.delete-content {
  padding: 24px;
  text-align: center;
}

.delete-icon-wrapper {
  margin-bottom: 16px;
}

.delete-message {
  font-size: 16px;
  font-weight: 500;
  color: #1f2937;
  margin: 0;
}

.file-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
}

.warning-text {
  font-size: 13px;
  color: #f97316;
  margin: 0;
}

.full-width-btn {
  width: 100%;
}

/* ========== Scrollbar ========== */
.preview-content::-webkit-scrollbar {
  width: 8px;
}

.preview-content::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 10px;
}

.preview-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.preview-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* ========== Animations ========== */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.file-list-item {
  animation: slideInUp 0.3s ease;
}
</style>